import * as focusTrap from 'focus-trap';
import classNames from 'classnames';
import { generateNavigation } from 'app/utilities/generate-navigation';
import Icon from './icon';
import PropTypes from 'prop-types';
import Tile from 'app/components/partials/tile/index';
import { NavLink, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

const Navigation = ({ activeNavItem, featuredShow, isMobileNavActive, mobileNavToggleHandler, navItemClickHandler }) => {
    const [generatedNav, setGeneratedNav] = useState(
        generateNavigation(null)
    );

    // Focus trap for mobile menu and submenu
    const mobileNavTrapRef = React.useRef(null);
    const mobileSubmenuTrapRef = React.useRef(null);
    // Guard: opening a submenu from the mobile menu
    const isOpeningSubmenuRef = React.useRef(false);

    // Add a ref to track the currently open submenu button for focus management
    const desktopSubmenuButtonRef = React.useRef(null);

    const location = useLocation();

    const isSubNavActive = (subItem) => {
        if (location.search || subItem.search) {
            return subItem.url === location.pathname && subItem.search === location.search;
        }

        return subItem.url === location.pathname;
    };

    // In mobile submenu, return to main menu without closing the whole mobile nav
    const mobileSubItemClickHandler = () => {
        navItemClickHandler(null);
    };

    const renderNavItemName = (name, maoriName) => (
        <div className="item-name-wrapper">
            <p className="item-name-maori" lang="mi">{maoriName}</p>
            <p className="item-name">{name}</p>
        </div>
    );

    const renderFeaturedSection = (title, type, data) => (
        <div className="featured-container">
            {title && <span className="h5 title">{title}</span>}
            <div className="tiles">
                {data.map((item, index) => <Tile key={index} type={type} isHorizontal={true} isHero={data.length === 1} {...item} />)}
            </div>
        </div>
    );

    const renderSubNavChildren = (item, index) => {
        if (item.name === 'divider') {
            return <li role="separator" key={index}><hr /></li>;
        }

        if (item.external) {
            return (
                <li key={index}>
                    <div className="sub-item">
                        <a href={item.url} target="_blank" rel="noreferrer">
                            <span className="h6 name">{item.name}</span>
                        </a>
                        {item.description && <p className="description">{item.description}</p>}
                        {item.icon && <Icon name={item.icon} />}
                    </div>
                </li>
            );
        }

        return (
            <li key={index}>
                <div className={classNames('sub-item', { 'is-active': isSubNavActive(item) })}>
                    <NavLink to={`${item.url}${item.search || ''}`}>
                        <span className="h6 name">{item.name}</span>
                    </NavLink>
                    {item.description && <p className="description">{item.description}</p>}
                    {item.icon && <Icon name={item.icon} />}
                </div>
            </li>
        );
    };

    const renderDesktopSubNav = (id) => {
        const { name, featured, children, subName } = activeNavItem;
        const subWrapperClass = classNames('sub-nav-container', {
            'has-featured': featured
        });

        return (
            <div id={id} className={subWrapperClass}>
                <div className="inner">
                    {featured && featured.data && renderFeaturedSection(featured.title, featured.type, featured.data)}
                    <ul className="sub-item-container">
                        {featured && <li className="sub-item-title"><span className="h5 title">{subName || name}</span></li>}
                        {children.map((item, index) => renderSubNavChildren(item, index))}
                    </ul>
                </div>
            </div>
        );
    };

    const renderDesktopNav = () => (
        <nav className="navigation-desktop">
            <ul className="nav-items">
                {generatedNav.map((navItem, index) => {
                    const isActive = activeNavItem && activeNavItem.name === navItem.name;
                    const navItemClass = classNames('nav-item', {
                        'is-active': isActive
                    });

                    if (navItem.url) {
                        return (
                            <li className="nav-item-wrapper" key={index}>
                                <NavLink
                                    className={({ isActive }) => classNames('nav-item', { 'is-active': isActive })}
                                    to={navItem.url}
                                >
                                    {renderNavItemName(navItem.name, navItem.maoriName)}
                                </NavLink>
                            </li>
                        );
                    }

                    const submenuId = `submenu-${index}`;

                    return (
                        <li className="nav-item-wrapper" key={index}>
                            <button
                                className={navItemClass}
                                aria-expanded={isActive ? 'true' : 'false'}
                                aria-controls={isActive ? submenuId : undefined}
                                onClick={(e) => {
                                    desktopSubmenuButtonRef.current = e.currentTarget;
                                    navItemClickHandler(navItem);
                                }}
                            >
                                {renderNavItemName(navItem.name, navItem.maoriName)}
                            </button>
                            {isActive && navItem.children && renderDesktopSubNav(submenuId)}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    // Mobile view
    const renderMobileSubNav = () => {
        const { name, maoriName, children, featured } = activeNavItem;

        return (
            <div id="mobile-submenu" className="navigation-mobile has-active">
                <button className="button no-border action-button" onClick={() => navItemClickHandler(null)}>
                    <Icon className="icon || left-arrow" name="chevron" /> Back
                </button>
                {renderNavItemName(name, maoriName)}
                {featured && featured.data && (
                    <div className="tiles">
                        {featured.data.map((item, index) => <Tile key={index} type={activeNavItem.featured.type} isHorizontal={true} onClick={mobileSubItemClickHandler} {...item} />)}
                    </div>
                )}
                <ul className="sub-nav-container">
                    {children && children.map((item, index) => {
                        if (item.name === 'divider') {
                            return <li role="separator" key={index}><hr /></li>;
                        }

                        if (item.external) {
                            return (
                                <li key={index}>
                                    <button className="sub-item" onClick={() => {
                                        window.open(item.url);
                                        mobileSubItemClickHandler();
                                    }}>
                                        <p className="name">{item.name}</p>
                                        {item.description && <p className="description">{item.description}</p>}
                                        {item.icon && <Icon name={item.icon} />}
                                    </button>
                                </li>
                            );
                        }

                        return (
                            <li key={index}>
                                <div className="sub-item">
                                    <NavLink to={item.url}>
                                        <p className="name">{item.name}</p>
                                    </NavLink>
                                    {item.description && <p className="description">{item.description}</p>}
                                    {item.icon && <Icon name={item.icon} />}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    const renderMobileNavItem = () => (
        <ul className={classNames('nav-items', { 'hidden': activeNavItem })}>
            {generatedNav.map((navItem, index) => {
                if (navItem.url) {
                    return (
                        <li key={index}>
                            <NavLink className="nav-item" to={navItem.url}>
                                {renderNavItemName(navItem.name, navItem.maoriName)}
                                <Icon name="chevron" />
                            </NavLink>
                        </li>
                    );
                }

                return (
                    <li key={index}>
                        <button
                            className="nav-item"
                            onClick={() => {
                                // Mark that we're transitioning into a submenu so the menu trap doesn't close the whole menu
                                isOpeningSubmenuRef.current = true;
                                navItemClickHandler(navItem);
                            }}
                        >
                            {renderNavItemName(navItem.name, navItem.maoriName)}
                            <Icon name="chevron" />
                        </button>
                    </li>
                );
            })}
        </ul>
    );

    const renderMobileNav = () => (
        <nav id="mobile-nav" className="navigation-mobile" role="dialog">
            <button className="button no-border action-button" onClick={mobileNavToggleHandler}>
                <Icon className="icon" name="close" />
            </button>
            {renderMobileNavItem()}
            {activeNavItem && renderMobileSubNav()}
        </nav>
    );

    useEffect(() => {
        setGeneratedNav(generateNavigation(featuredShow));
    }, [featuredShow]);

    // Add effect to listen for Escape key when a submenu is open in desktop view
    useEffect(() => {
        if (isMobileNavActive || !activeNavItem) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                navItemClickHandler(null);
                // Return focus to the button that opened the submenu
                if (desktopSubmenuButtonRef.current) {
                    desktopSubmenuButtonRef.current.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMobileNavActive, activeNavItem, navItemClickHandler]);

    // Manage focus trap for mobile navigation menu (when open and no submenu)
    useEffect(() => {
        if (!isMobileNavActive || activeNavItem) {
            return;
        }

        // Clean up any existing trap first
        if (mobileNavTrapRef.current) {
            try {
                mobileNavTrapRef.current.deactivate();
            } catch (e) { /* noop */ }
            mobileNavTrapRef.current = null;
        }

        const container = document.getElementById('mobile-nav');
        if (container) {
            mobileNavTrapRef.current = focusTrap.createFocusTrap(container, {
                escapeDeactivates: true,
                clickOutsideDeactivates: true,
                returnFocusOnDeactivate: true,
                fallbackFocus: container,
                onDeactivate: () => {
                    // If we're opening a submenu, don't close the whole menu
                    if (isOpeningSubmenuRef.current) {
                        isOpeningSubmenuRef.current = false;

                        return;
                    }
                    mobileNavToggleHandler();
                }
            });
            mobileNavTrapRef.current.activate();
        }

        return () => {
            if (mobileNavTrapRef.current) {
                try {
                    mobileNavTrapRef.current.deactivate();
                } catch (e) { /* noop */ }
                mobileNavTrapRef.current = null;
            }
        };
    }, [isMobileNavActive, activeNavItem]);

    // Manage focus trap for mobile navigation submenu (when submenu is open)
    useEffect(() => {
        if (!isMobileNavActive || !activeNavItem) {
            return;
        }

        // Clean up any existing trap first
        if (mobileSubmenuTrapRef.current) {
            try {
                mobileSubmenuTrapRef.current.deactivate();
            } catch (e) { /* noop */ }
            mobileSubmenuTrapRef.current = null;
        }

        const container = document.getElementById('mobile-submenu');
        if (container) {
            mobileSubmenuTrapRef.current = focusTrap.createFocusTrap(container, {
                escapeDeactivates: true,
                clickOutsideDeactivates: true,
                returnFocusOnDeactivate: true,
                fallbackFocus: container,
                onDeactivate: () => {
                    // Close only the submenu and return to the menu
                    if (mobileSubmenuTrapRef.current) {
                        try {
                            mobileSubmenuTrapRef.current.deactivate();
                        } catch (e) { /* noop */ }
                        mobileSubmenuTrapRef.current = null;
                    }
                    navItemClickHandler(null);
                }
            });
            mobileSubmenuTrapRef.current.activate();
        }

        return () => {
            if (mobileSubmenuTrapRef.current) {
                try {
                    mobileSubmenuTrapRef.current.deactivate();
                } catch (e) { /* noop */ }
                mobileSubmenuTrapRef.current = null;
            }
        };
    }, [isMobileNavActive, activeNavItem, generatedNav]);

    return (
        <React.Fragment>
            {generatedNav && renderDesktopNav()}
            {generatedNav && isMobileNavActive && renderMobileNav()}
        </React.Fragment>
    );
};

Navigation.propTypes = {
    activeNavItem: PropTypes.object,
    featuredShow: PropTypes.object,
    isMobileNavActive: PropTypes.bool.isRequired,
    mobileNavToggleHandler: PropTypes.func.isRequired,
    navItemClickHandler: PropTypes.func.isRequired,
};

export default Navigation;
